import { useTranslation } from "react-i18next";
import SurveyDispatch from "../../models/surveyDispatch";
import { darkStoneBlue } from "../../styles/theme";
import { GetDemographicGroupsResponse } from "../../types/workplaceCulture";
import { getOrganizationDemographic } from "../../utils/WorkplaceCulture.utils";
import { getFormattedDate } from "../../utils/formatters";
import i18n from "../../utils/i18n";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./ReportMethodology.scss";

interface ReportMethodologyProps {
  surveyDispatch: SurveyDispatch;
  demographicsData?: GetDemographicGroupsResponse;
}

export const ReportMethodology = ({
  surveyDispatch,
  demographicsData,
}: ReportMethodologyProps) => {
  const { t } = useTranslation("reportPage");

  const organisationDemographic = getOrganizationDemographic(demographicsData);
  const participantCount = organisationDemographic?.engagement || 0;
  const totalRecipients = surveyDispatch.recipientsCount || 1;
  const responseRate = participantCount
    ? `${((participantCount / totalRecipients) * 100).toFixed(0)}%`
    : "0%";

  const launchDate = getFormattedDate(
    surveyDispatch.launchDate || "",
    t,
    i18n.language
  );

  const dueDate = getFormattedDate(
    surveyDispatch.dueDate || "",
    t,
    i18n.language
  );

  return (
    <div className="ReportMethodology">
      <Paper shadow>
        <Typography
          tagVariant="h2"
          desktop="h2"
          className="ReportMethodology__title"
        >
          {t("methodology.title")}
        </Typography>

        <div className="ReportMethodology__metrics">
          <Paper className="ReportMethodology__metrics__card">
            <Typography
              desktop="body1"
              weight="bold"
              className="ReportMethodology__metrics__card__label"
            >
              {t("methodology.responses")}
            </Typography>
            <Typography
              desktop="h2"
              className="ReportMethodology__metrics__card__value"
            >
              {participantCount}
            </Typography>
          </Paper>

          <Paper className="ReportMethodology__metrics__card">
            <Typography
              desktop="body1"
              weight="bold"
              className="ReportMethodology__metrics__card__label"
            >
              {t("methodology.responseRate")}
            </Typography>
            <Typography
              desktop="h2"
              className="ReportMethodology__metrics__card__value"
            >
              {responseRate}
            </Typography>
          </Paper>
        </div>

        <Paper className="ReportMethodology__infoCard">
          <div className="ReportMethodology__infoContent">
            <div
              className="ReportMethodology__infoText"
              dangerouslySetInnerHTML={{
                __html: t("methodology.surveyOpenText", {
                  launchDate,
                  dueDate,
                }),
              }}
            ></div>
            <Icon
              type={IconTypes.Shuttle}
              className="ReportMethodology__icon"
              color={darkStoneBlue}
              size={24}
            />
          </div>
        </Paper>

        <Paper className="ReportMethodology__infoCard">
          <div className="ReportMethodology__infoContent">
            <div
              className="ReportMethodology__infoText"
              dangerouslySetInnerHTML={{
                __html: t("methodology.languagesText"),
              }}
            />
            <Icon
              type={IconTypes.Globe}
              className="ReportMethodology__icon"
              size={24}
            />
          </div>
        </Paper>

        <Paper className="ReportMethodology__infoCard">
          <div className="ReportMethodology__infoContent">
            <Typography desktop="body2" className="ReportMethodology__infoText">
              {t("methodology.insightsDisclaimer")}
            </Typography>
          </div>
        </Paper>
      </Paper>
    </div>
  );
};
